import { Button, Select, Space } from 'antd'
import { Dropdown } from 'components/form/inputs/Dropdown'
import { forecastMath } from 'features/math'
import { sanitizeSensorId } from 'features/timeSeries'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { FormulaToken, FormulaTokenElement } from './FormulaTokenElement'

interface FormulaBuilderProps {
  sensors: Array<{ id: string; name: string }>
  value: string
  onChange: (expression: string) => void
  onError?: (error: string | null) => void
  disabled?: boolean
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const FormulaDisplay = styled.div<{ $disabled?: boolean }>`
  min-height: 2.5rem;
  padding: 0.5rem;
  background: ${({ theme }) => theme.forecast.background3};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.25rem;
  display: flex;
  flex-wrap: wrap;
  column-gap: 0.25rem;
  align-items: center;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'text')};
  position: relative;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  &:focus-within {
    border-color: ${({ theme, $disabled }) =>
      $disabled ? theme.forecast.border : theme.forecast.primary};
    box-shadow: ${({ theme, $disabled }) =>
      $disabled ? 'none' : `0 0 0 0.125rem ${theme.forecast.primary}20`};
  }
`

const InlineInput = styled.input<{ $disabled?: boolean }>`
  border: none;
  outline: none;
  background: transparent;
  color: inherit;
  padding: 0;
  margin: 0;
  width: ${(props) =>
    props.value ? `${props.value.toString().length + 1}ch` : '1ch'};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'text')};
`

const SensorList = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: 0.25rem;
  margin-top: 0.25rem;
`

const HelpText = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.text2};
`

const FUNCTIONS = [
  'sqrt',
  'abs',
  'sin',
  'cos',
  'tan',
  'log',
  'log10',
  'exp',
  'round',
  'floor',
  'ceil',
  'min',
  'max',
]

const UNIT_CONVERSIONS = [
  {
    category: 'Temperature',
    conversions: [
      { id: 'c_to_f', name: '°C to °F', formula: '(VALUE * 9 / 5) + 32' },
      { id: 'f_to_c', name: '°F to °C', formula: '(VALUE - 32) * 5 / 9' },
    ],
  },
  {
    category: 'Pressure',
    conversions: [
      { id: 'psi_to_kpa', name: 'psi to kPa', formula: 'VALUE * 6.89476' },
      { id: 'kpa_to_psi', name: 'kPa to psi', formula: 'VALUE * 0.145038' },
      { id: 'bar_to_psi', name: 'bar to psi', formula: 'VALUE * 14.5038' },
      { id: 'psi_to_bar', name: 'psi to bar', formula: 'VALUE * 0.0689476' },
      { id: 'psi_to_inh2o', name: 'psi to inH₂O', formula: 'VALUE * 27.7076' },
      {
        id: 'inh2o_to_psi',
        name: 'inH₂O to psi',
        formula: 'VALUE * 0.0360912',
      },
    ],
  },
]

const createToken = (
  sensors: Array<{ id: string; name: string }>,
  current: string
): FormulaToken | null => {
  if (!current) return null
  // Check if current is a sensor name or sanitized sensor ID
  const sensor = sensors.find(
    (s) => s.name === current || sanitizeSensorId(s.id) === current
  )

  if (sensor) {
    return {
      type: 'sensor',
      value: sanitizeSensorId(sensor.id),
      displayValue: sensor.name,
    }
  } else if (!isNaN(Number(current))) {
    return { type: 'number', value: current }
  } else if (isFunction(current)) {
    return { type: 'function', value: current }
  } else if (current === 'VALUE') {
    return { type: 'placeholder', value: current }
  }
  return null
}

const isOperator = (char: string): boolean => {
  return '+-*/^()'.includes(char)
}

const isFunction = (str: string): boolean => {
  return FUNCTIONS.includes(str)
}

const parseExpressionToTokens = (
  expression: string,
  sensors: Array<{ id: string; name: string }>
): FormulaToken[] => {
  const tokens: FormulaToken[] = []
  let current = ''
  let i = 0

  while (i < expression.length) {
    const char = expression[i]

    if (char === ' ' || isOperator(char)) {
      const newToken = createToken(sensors, current)
      if (newToken) tokens.push(newToken)
      if (isOperator(char)) {
        tokens.push({
          type: char === '(' || char === ')' ? 'parenthesis' : 'operator',
          value: char,
        })
      }
      current = ''
    } else {
      current += char
    }
    i++
  }

  const newToken = createToken(sensors, current)
  if (newToken) tokens.push(newToken)

  return tokens
}

export const FormulaBuilder: React.FC<FormulaBuilderProps> = ({
  sensors,
  value,
  onChange,
  onError,
  disabled = false,
}) => {
  const { parse } = forecastMath
  const tokenIdCounter = useRef(0)
  const tokenIds = useRef<number[]>([])
  const hiddenInputRef = useRef<HTMLInputElement>(null)
  const lastExternalValue = useRef<string>(value)

  const [tokens, setTokens] = useState<FormulaToken[]>(() => {
    if (!value) return []
    const parsedTokens = parseExpressionToTokens(value, sensors)
    tokenIds.current = parsedTokens.map(() => tokenIdCounter.current++)
    return parsedTokens
  })
  const [cursorPosition, setCursorPosition] = useState(tokens.length)
  const [inputValue, setInputValue] = useState('')

  const tokensToExpression = useCallback(
    (tokenList: FormulaToken[]): string => {
      return tokenList.map((token) => token.value).join(' ')
    },
    []
  )

  // Sync tokens when value prop changes externally (e.g., when editing an existing formula)
  // Only update if the value prop changed from outside, not from our own onChange calls
  useEffect(() => {
    if (value !== lastExternalValue.current) {
      lastExternalValue.current = value
      if (!value) {
        setTokens([])
        tokenIds.current = []
        setCursorPosition(0)
      } else {
        const parsedTokens = parseExpressionToTokens(value, sensors)
        setTokens(parsedTokens)
        tokenIds.current = parsedTokens.map(() => tokenIdCounter.current++)
        setCursorPosition(parsedTokens.length)
      }
    }
  }, [value, sensors])

  const validateExpression = useCallback(
    (tokenList: FormulaToken[]) => {
      const expression = tokensToExpression(tokenList)
      if (!expression.trim()) {
        onError?.(null)
        return true
      }

      try {
        parse(expression)
        onError?.(null)
        return true
      } catch (error) {
        onError?.(error instanceof Error ? error.message : 'Invalid expression')
        return false
      }
    },
    [tokensToExpression, onError, parse]
  )

  useEffect(() => {
    const expression = tokensToExpression(tokens)
    onChange(expression)
    validateExpression(tokens)
  }, [tokens, tokensToExpression, validateExpression, onChange])

  const addToken = useCallback(
    (token: FormulaToken) => {
      setTokens((prevTokens) => {
        const newTokens = [...prevTokens]
        newTokens.splice(cursorPosition, 0, token)
        return newTokens
      })
      tokenIds.current.splice(cursorPosition, 0, tokenIdCounter.current)
      tokenIdCounter.current += 1
      setCursorPosition((prev) => prev + 1)
    },
    [cursorPosition]
  )

  const removeToken = useCallback(
    (index: number) => {
      setTokens((prevTokens) => prevTokens.filter((_, i) => i !== index))
      tokenIds.current = tokenIds.current.filter((_, i) => i !== index)
      setCursorPosition((prev) => {
        // If removing a token before the cursor, move cursor back
        if (index < prev) {
          return Math.max(0, prev - 1)
        }
        // If removing at cursor position (e.g., placeholder click), keep cursor at same index
        return Math.min(prev, tokens.length - 1)
      })
    },
    [tokens.length]
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      const prevValue = inputValue
      setInputValue(value)

      // Parse what was typed
      if (value.length > prevValue.length) {
        const lastChar = value[value.length - 1]

        if (isOperator(lastChar) || lastChar === ' ') {
          const textBeforeChar = value.slice(0, -1).trim()

          if (textBeforeChar) {
            const token = createToken(sensors, textBeforeChar)
            if (token) {
              addToken(token)
            }
          }

          if (isOperator(lastChar)) {
            addToken({
              type:
                lastChar === '(' || lastChar === ')'
                  ? 'parenthesis'
                  : 'operator',
              value: lastChar,
            })
          }

          setInputValue('')
        }
      }
    },
    [addToken, sensors, inputValue]
  )

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowLeft' && inputValue === '') {
        e.preventDefault()
        setCursorPosition(Math.max(0, cursorPosition - 1))
      } else if (e.key === 'ArrowRight' && inputValue === '') {
        e.preventDefault()
        setCursorPosition(Math.min(tokens.length, cursorPosition + 1))
      } else if (
        e.key === 'Backspace' &&
        inputValue === '' &&
        cursorPosition > 0
      ) {
        e.preventDefault()
        removeToken(cursorPosition - 1)
      } else if (
        e.key === 'Delete' &&
        inputValue === '' &&
        cursorPosition < tokens.length
      ) {
        e.preventDefault()
        removeToken(cursorPosition)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        // Try to parse current input as a sensor
        const trimmed = inputValue.trim()
        if (trimmed) {
          const sensor = sensors.find(
            (s) => s.name.toLowerCase() === trimmed.toLowerCase()
          )
          if (sensor) {
            addToken({
              type: 'sensor',
              value: sanitizeSensorId(sensor.id),
              displayValue: sensor.name,
            })
            setInputValue('')
          }
        }
      }
    },
    [inputValue, cursorPosition, tokens.length, removeToken, sensors, addToken]
  )

  // Insert sensor
  const insertSensor = useCallback(
    (sensor: { id: string; name: string }) => {
      addToken({
        type: 'sensor',
        value: sanitizeSensorId(sensor.id),
        displayValue: sensor.name,
      })
      hiddenInputRef.current?.focus()
    },
    [addToken]
  )

  // Insert unit conversion
  const insertUnitConversion = useCallback(
    (formula: string) => {
      const conversionTokens = parseExpressionToTokens(formula, sensors)

      setTokens((prevTokens) => {
        const newTokens = [...prevTokens]
        conversionTokens.forEach((token, index) => {
          newTokens.splice(cursorPosition + index, 0, token)
        })
        return newTokens
      })

      const newIds = conversionTokens.map(() => {
        const id = tokenIdCounter.current
        tokenIdCounter.current += 1
        return id
      })
      tokenIds.current.splice(cursorPosition, 0, ...newIds)

      setCursorPosition((prev) => prev + conversionTokens.length)
      hiddenInputRef.current?.focus()
    },
    [cursorPosition, sensors]
  )

  const handlePlaceholderClick = useCallback(
    (token: FormulaToken, index: number) => {
      // If clicking on a placeholder, remove it and position cursor there
      if (token.type === 'placeholder') {
        removeToken(index)
        setCursorPosition(index)
        // Focus the input after a brief delay to ensure DOM updates
        setTimeout(() => hiddenInputRef.current?.focus(), 0)
      } else {
        setCursorPosition(index + 1)
      }
    },
    [removeToken]
  )

  const renderTokensWithCursor = useMemo(() => {
    const elements: React.ReactNode[] = []

    tokens.forEach((token, index) => {
      if (index === cursorPosition) {
        elements.push(
          <InlineInput
            key={`input-${index}`}
            ref={index === cursorPosition ? hiddenInputRef : undefined}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder=''
            disabled={disabled}
            $disabled={disabled}
          />
        )
      }

      elements.push(
        <FormulaTokenElement
          key={`token-${tokenIds.current[index]}`}
          token={token}
          index={index}
          onRemove={removeToken}
          onPlaceholderClick={handlePlaceholderClick}
        />
      )
    })

    if (cursorPosition === tokens.length) {
      elements.push(
        <InlineInput
          key='input-end'
          ref={cursorPosition === tokens.length ? hiddenInputRef : undefined}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder=''
          disabled={disabled}
          $disabled={disabled}
        />
      )
    }

    return elements
  }, [
    tokens,
    cursorPosition,
    inputValue,
    handleInputChange,
    handleKeyDown,
    removeToken,
    handlePlaceholderClick,
    disabled,
  ])

  const addConversion = (value: string) => {
    const conversionInfo = UNIT_CONVERSIONS.flatMap(
      (cat) => cat.conversions
    ).find((c) => c.id === value)
    if (conversionInfo) {
      insertUnitConversion(conversionInfo.formula)
    }
  }

  const handleSensorSelection = useCallback(
    (sensorId: string) => {
      const sensor = sensors.find((s) => s.id === sensorId)
      if (sensor) {
        insertSensor(sensor)
      }
    },
    [sensors, insertSensor]
  )

  const sensorOptions = useMemo(
    () =>
      sensors.map((sensor) => ({
        label: sensor.name,
        value: sensor.id,
      })),
    [sensors]
  )

  return (
    <Container>
      <FormulaDisplay
        $disabled={disabled}
        onClick={() => !disabled && hiddenInputRef.current?.focus()}>
        {renderTokensWithCursor}
      </FormulaDisplay>

      <div>
        <HelpText>Select a sensor to insert:</HelpText>
        <Dropdown
          options={sensorOptions}
          value={undefined}
          onValueChange={handleSensorSelection}
          placeholder='Select sensor'
          mode='edit'
          cleanable={false}
          disabled={disabled}
        />
      </div>

      <SensorList>
        <HelpText>Unit conversions:</HelpText>
        <Select
          size='small'
          placeholder='Insert conversion'
          onChange={addConversion}
          value={undefined}
          popupMatchSelectWidth={false}
          disabled={disabled}>
          {UNIT_CONVERSIONS.map((category) => (
            <Select.OptGroup key={category.category} label={category.category}>
              {category.conversions.map((conversion) => (
                <Select.Option key={conversion.id} value={conversion.id}>
                  {conversion.name}
                </Select.Option>
              ))}
            </Select.OptGroup>
          ))}
        </Select>
      </SensorList>

      <Space orientation='vertical' size='small'>
        <HelpText>
          • Type numbers and operators (+, -, *, /, ^) directly
        </HelpText>
        <HelpText>
          • Type sensor names and press space or Enter to add them
        </HelpText>
        <HelpText>• Functions: sqrt, abs, sin, cos, log, exp, etc.</HelpText>
        <Button
          size='small'
          danger
          onClick={() => {
            setTokens([])
            setCursorPosition(0)
            setInputValue('')
            tokenIds.current = []
            tokenIdCounter.current = 0
            onChange('')
          }}
          disabled={disabled}>
          Clear
        </Button>
      </Space>
    </Container>
  )
}
